/**
 * Cupons de desconto (spec 016; Vitor, 18/09: "implemente ja a questao dos cupons de desconto que tinha no legado").
 * O cupom vale nos checkouts que a equipe marcar, com validade e limite de usos. "Mostrar no checkout" faz a propria
 * pagina oferecer ("voce tem um cupom"); sem isso, so funciona pra quem digita o codigo.
 */
import { Aviso, Botao, CabecalhoDePagina, Campo, CampoCaixa, CampoData, CampoDinheiro, Carregando, Pilha, Segmentado, Selo, Tabela, Vazio, diaEmSaoPaulo, formatarDia, formatarReais, soDigitos } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { config } from "../config";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";

const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;

type Cupom = {
  id: string;
  codigo: string;
  descricao: string | null;
  tipo: "PERCENTUAL" | "VALOR";
  percentual: number | null;
  valor_centavos: number | null;
  comeca_em: string | null;
  termina_em: string | null;
  max_usos: number | null;
  reservados: number;
  usados: number;
  ativo: boolean;
  mostrar_no_checkout: boolean;
  checkouts: { id: string; nome: string; token: string }[];
};
type CheckoutDaLista = { id: string; nome: string; ativo: boolean };

/** "30%" ou "R$ 100,00". */
const desconto = (c: Pick<Cupom, "tipo" | "percentual" | "valor_centavos">) => (c.tipo === "PERCENTUAL" ? `${c.percentual ?? 0}%` : formatarReais(c.valor_centavos ?? 0));
// O dia de Brasilia, nao o de UTC: o fim do cupom e 02:59:59Z do dia seguinte e cada edicao empurrava um dia (19/10).
const soDia = (iso: string | null) => (iso ? diaEmSaoPaulo(iso) : "");
const validade = (c: Cupom) => {
  const de = c.comeca_em ? formatarDia(soDia(c.comeca_em)) : null;
  const ate = c.termina_em ? formatarDia(soDia(c.termina_em)) : null;
  return de && ate ? `${de} a ${ate}` : ate ? `Até ${ate}` : de ? `A partir de ${de}` : "Sem prazo";
};
const usos = (c: Cupom) => (c.max_usos ? `${c.usados} de ${c.max_usos}${c.reservados ? `, ${c.reservados} em aberto` : ""}` : `${c.usados}${c.reservados ? `, ${c.reservados} em aberto` : ""}`);

type Formulario = { codigo: string; descricao: string; tipo: "PERCENTUAL" | "VALOR"; percentual: string; valor: number; comeca: string; termina: string; limitado: boolean; maxUsos: string; mostrar: boolean; checkouts: string[] };
const vazio: Formulario = { codigo: "", descricao: "", tipo: "PERCENTUAL", percentual: "10", valor: 0, comeca: "", termina: "", limitado: false, maxUsos: "50", mostrar: false, checkouts: [] };
const doCupom = (c: Cupom): Formulario => ({
  codigo: c.codigo,
  descricao: c.descricao ?? "",
  tipo: c.tipo,
  percentual: String(c.percentual ?? 10),
  valor: c.valor_centavos ?? 0,
  comeca: soDia(c.comeca_em),
  termina: soDia(c.termina_em),
  limitado: c.max_usos !== null,
  maxUsos: String(c.max_usos ?? 50),
  mostrar: c.mostrar_no_checkout,
  checkouts: c.checkouts.map((x) => x.id),
});
const pronto = (f: Formulario) =>
  f.codigo.trim().length >= 2 &&
  f.checkouts.length > 0 &&
  (f.tipo === "PERCENTUAL" ? Number(f.percentual) >= 1 && Number(f.percentual) <= 90 : f.valor > 0) &&
  (!f.limitado || Number(f.maxUsos) >= 1) &&
  (!f.comeca || !f.termina || f.comeca < f.termina);
/** O dia vira instante em Sao Paulo: comeca no primeiro segundo, termina no fim do dia. */
const instante = (dia: string, fim: boolean) => (dia ? new Date(`${dia}T${fim ? "23:59:59" : "00:00:00"}-03:00`).toISOString() : null);
const corpo = (f: Formulario) => ({
  codigo: f.codigo.trim(),
  descricao: f.descricao.trim() || null,
  tipo: f.tipo,
  percentual: f.tipo === "PERCENTUAL" ? Number(f.percentual) : null,
  valor_centavos: f.tipo === "VALOR" ? f.valor : null,
  comeca_em: instante(f.comeca, false),
  termina_em: instante(f.termina, true),
  max_usos: f.limitado ? Number(f.maxUsos) : null,
  mostrar_no_checkout: f.mostrar,
  checkouts: f.checkouts,
});

function CamposDoCupom({ f, setF, checkouts }: { f: Formulario; setF: (f: Formulario) => void; checkouts: CheckoutDaLista[] }) {
  return (
    <>
      <Campo rotulo="Código" valor={f.codigo} aoMudar={(v) => setF({ ...f, codigo: v.toUpperCase().replace(/\s+/g, "") })} mono dica="É o que o comprador digita. Vira maiúsculo sozinho." required />
      <Campo rotulo="Descrição (só pra equipe)" valor={f.descricao} aoMudar={(v) => setF({ ...f, descricao: v })} placeholder="Ex.: campanha de setembro, parceria com o influenciador X" />
      <Segmentado rotulo="Desconto" nome="tipo-do-cupom" valor={f.tipo} aoMudar={(v) => setF({ ...f, tipo: v })} opcoes={[{ valor: "PERCENTUAL", rotulo: "Porcentagem" }, { valor: "VALOR", rotulo: "Valor fixo" }]} />
      {f.tipo === "PERCENTUAL" ? (
        <Campo rotulo="Porcentagem de desconto" valor={f.percentual} aoMudar={(v) => setF({ ...f, percentual: soDigitos(v).slice(0, 2) })} inputMode="numeric" mono dica="De 1 a 90. Incide sobre o plano mais os benefícios comprados." />
      ) : (
        <CampoDinheiro rotulo="Valor do desconto" centavos={f.valor} aoMudar={(v) => setF({ ...f, valor: v })} dica="Descontado do total da compra." />
      )}
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))" }}>
        <CampoData rotulo="Começa em (opcional)" valor={f.comeca} aoMudar={(v) => setF({ ...f, comeca: v })} />
        <CampoData rotulo="Termina em (opcional)" valor={f.termina} aoMudar={(v) => setF({ ...f, termina: v })} dica="Vale até o fim deste dia." />
      </div>
      <CampoCaixa marcado={f.limitado} aoMudar={(v) => setF({ ...f, limitado: v })}>Limitar a quantidade de usos</CampoCaixa>
      {f.limitado && <Campo rotulo="Máximo de usos" valor={f.maxUsos} aoMudar={(v) => setF({ ...f, maxUsos: soDigitos(v).slice(0, 5) })} inputMode="numeric" mono dica="Quem abre o pagamento já segura um uso; se não pagar e a sessão expirar, ele volta." />}
      <CampoCaixa marcado={f.mostrar} aoMudar={(v) => setF({ ...f, mostrar: v })}>Oferecer na página do checkout</CampoCaixa>
      <span className="mtc-campo__dica">Marcado, a própria página oferece o desconto a quem abrir o link. Desmarcado, só funciona pra quem sabe o código (parceiro, influenciador).</span>
      <Pilha espaco={2}>
        <span className="mtc-rotulo">Em quais checkouts vale</span>
        {checkouts.length === 0 ? (
          <span className="mtc-campo__dica">Nenhum checkout ativo. Crie em Financeiro, Checkouts.</span>
        ) : (
          checkouts.map((c) => (
            <CampoCaixa key={c.id} marcado={f.checkouts.includes(c.id)} aoMudar={(v) => setF({ ...f, checkouts: v ? [...f.checkouts, c.id] : f.checkouts.filter((x) => x !== c.id) })}>
              {c.nome}
            </CampoCaixa>
          ))
        )}
        <span className="mtc-campo__dica">O cupom só funciona nos checkouts marcados.</span>
      </Pilha>
    </>
  );
}

export function PaginaCupons() {
  const pag = usePagina("pagina");
  const { ler, postar, por } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const admin = pode("ADMIN");
  const lista = useQuery({ queryKey: ["cupons"], queryFn: () => ler<{ itens: Cupom[] }>(PAG("/v1/admin/cupons")) });
  const checkouts = useQuery({ queryKey: ["checkouts"], queryFn: () => ler<{ itens: CheckoutDaLista[] }>(PAG("/v1/admin/checkouts")) });
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<Cupom | null>(null);
  const [desativando, setDesativando] = useState<Cupom | null>(null);
  const [f, setF] = useState<Formulario>(vazio);
  const [feito, setFeito] = useState<string>();
  const ativos = (checkouts.data?.itens ?? []).filter((c) => c.ativo);
  const recarregar = () => qc.invalidateQueries({ queryKey: ["cupons"] });

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Cupons"
        descricao="Desconto no checkout, como no sistema antigo. O cupom vale nos checkouts que você marcar, pode ter validade e limite de usos, e o desconto é sempre calculado no servidor."
        acoes={admin ? <Botao onClick={() => { setFeito(undefined); setF(vazio); setNovo(true); }}>Novo cupom</Botao> : undefined}
      />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {!admin && <Aviso tipo="info">Criar e mudar cupom é do admin pra cima. Você pode ver a lista e os usos.</Aviso>}
      {lista.isLoading ? <Carregando compacto texto="Buscando os cupons" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Tabela
          paginacao={pag}
          linhas={lista.data?.itens ?? []}
          chave={(c) => c.id}
          cartao="media"
          vazio={<Vazio titulo="Nenhum cupom">Crie o primeiro e marque em quais checkouts ele vale.</Vazio>}
          colunas={[
            { chave: "codigo", rotulo: "Código", celula: (c) => <><span className="mtc-mono">{c.codigo}</span>{c.descricao && <span className="mtc-tabela__apoio">{c.descricao}</span>}</> },
            { chave: "desconto", rotulo: "Desconto", celula: (c) => <>{desconto(c)}<span className="mtc-tabela__apoio">{c.tipo === "PERCENTUAL" ? "sobre o total" : "do total"}</span></> },
            { chave: "validade", rotulo: "Validade", celula: (c) => validade(c) },
            { chave: "usos", rotulo: "Usos", celula: (c) => usos(c) },
            { chave: "onde", rotulo: "Onde vale", celula: (c) => <>{c.checkouts.length === 1 ? c.checkouts[0]!.nome : `${c.checkouts.length} checkouts`}<span className="mtc-tabela__apoio">{c.mostrar_no_checkout ? "Aparece na página" : "Só com o código"}</span></> },
            { chave: "situacao", rotulo: "Situação", celula: (c) => <Selo tom={c.ativo ? "sucesso" : "neutro"}>{c.ativo ? "Ativo" : "Desativado"}</Selo> },
            ...(admin
              ? [{
                  chave: "acoes",
                  rotulo: "Ações",
                  rotuloOculto: true,
                  celula: (c: Cupom) => (
                    <span className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "nowrap" }}>
                      <Botao tamanho="pequeno" variante="secundario" onClick={() => { setFeito(undefined); setF(doCupom(c)); setEditando(c); }}>Editar</Botao>
                      <Botao tamanho="pequeno" variante="fantasma" onClick={() => { setFeito(undefined); setDesativando(c); }}>{c.ativo ? "Desativar" : "Ativar"}</Botao>
                    </span>
                  ),
                }]
              : []),
          ]}
        />
      )}

      <DialogoDeAcao
        aberto={novo}
        aoFechar={() => setNovo(false)}
        titulo="Novo cupom"
        descricao="O comprador digita o código no checkout e o desconto sai no total, nas parcelas e no recibo."
        botao="Criar cupom"
        podeEnviar={pronto(f)}
        aoEnviar={() => postar(PAG("/v1/admin/cupons"), corpo(f))}
        aoConcluir={() => { void recarregar(); setNovo(false); setFeito(`Cupom ${f.codigo.trim().toUpperCase()} criado.`); }}
      >
        <CamposDoCupom f={f} setF={setF} checkouts={ativos} />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={Boolean(editando)}
        aoFechar={() => setEditando(null)}
        titulo={`Cupom ${editando?.codigo ?? ""}`}
        descricao="A mudança vale pras próximas compras. As que já foram feitas com ele continuam como estão."
        botao="Salvar"
        podeEnviar={pronto(f)}
        aoEnviar={() => por(PAG(`/v1/admin/cupons/${editando!.id}`), corpo(f))}
        aoConcluir={() => { void recarregar(); setEditando(null); setFeito("Cupom salvo."); }}
      >
        <CamposDoCupom f={f} setF={setF} checkouts={ativos} />
      </DialogoDeAcao>

      <DialogoDeAcao
        aberto={Boolean(desativando)}
        aoFechar={() => setDesativando(null)}
        titulo={desativando?.ativo ? `Desativar ${desativando.codigo}` : `Ativar ${desativando?.codigo ?? ""}`}
        descricao={desativando?.ativo ? "Ele para de funcionar no checkout na hora. As compras que já usaram continuam como estão." : "Ele volta a funcionar nos checkouts marcados, respeitando validade e limite."}
        botao={desativando?.ativo ? "Desativar" : "Ativar"}
        variante={desativando?.ativo ? "perigo" : "primario"}
        aoEnviar={() => postar(PAG(`/v1/admin/cupons/${desativando!.id}/ativo`), { ativo: !desativando!.ativo })}
        aoConcluir={() => { void recarregar(); setFeito(desativando?.ativo ? "Cupom desativado." : "Cupom ativado."); setDesativando(null); }}
      />
    </Pilha>
  );
}
